import { createRoot, type Root } from "react-dom/client";
import { createElement, useLayoutEffect, type ReactNode } from "react";

/**
 * Pooled React roots for SlickGrid (v10) cells.
 *
 * Problem: formatters mount React via `createRoot()` once per cell. SlickGrid
 * virtualises — every time a row scrolls into view it rebuilds fresh cell nodes
 * and re-runs the formatter — so a scroll session used to create tens of
 * thousands of roots (CPU-profiled: 47k `createRoot` calls ≈ 5.4s of synchronous
 * main-thread time on the 872-row Files grid), and none were ever unmounted.
 *
 * Fix: a global pool of React roots REUSED across cells. Each root is bound
 * permanently to a `display:contents` inner wrapper (generates no layout box, so
 * moving it between cells has zero visual effect). The formatter's own container
 * (with its styles) is untouched; the inner is appended inside it. `createRoot`
 * is paid only to grow the pool to the working-set size (~visible cells).
 *
 * Lifecycle is driven purely by DOM connectivity — NO SlickGrid hooks:
 *  - When SlickGrid recycles a row, the cell's container becomes `!isConnected`;
 *    a MICROTASK-deferred sweep ({@link scheduleSweep}) reclaims those roots into
 *    the pool. The sweep MUST NOT run synchronously inside {@link mountCellReact}:
 *    SlickGrid builds new rows in a detached fragment and attaches them only at
 *    the END of its render pass, so during that task the just-mounted neighbouring
 *    cells are still `!isConnected` — a synchronous sweep "reclaims" them and
 *    steals their inner out of cells that are about to appear on screen (observed
 *    as a mostly-blank grid + other cells' content shuffled across columns). A
 *    microtask runs after SlickGrid's task, when its rows are attached, so only
 *    truly recycled cells are reclaimed.
 *  - Reclaim does NOT clear the root (that would double the React work on the
 *    scroll hot path) — reuse renders the next element directly over the old tree
 *    in one reconcile pass. A reused DIRTY entry is kept `visibility:hidden` until
 *    that render COMMITS ({@link RevealOnCommit}, layout effect = before paint),
 *    so the old tree's content can never appear inside the new cell, even when
 *    React's scheduler delays the commit past a paint. Parked entries are cleared
 *    during idle ({@link clearParked}).
 *  - {@link unmountAllCellRoots} reclaims everything on grid destroy.
 *
 * Deliberately NOT used: SlickGrid's `enableAsyncPostRender(Cleanup)` machinery.
 * It was wired here originally to get per-cell teardown callbacks, but its
 * cleanup queue grows unbounded whenever the row-recycle rate exceeds its fixed
 * drain rate (any fast scroll) while each drain tick does O(queue) `Array.shift`
 * work — measured as "the grid lags more the longer you scroll". Connectivity
 * sweeps need none of it. Keep those grid options OFF in both wrappers.
 */

type Entry = {
  inner: HTMLElement;
  root: Root;
  host: HTMLElement | null;
  /** Old fiber tree still mounted (reclaimed without clearing) — cleared during idle. */
  dirty?: boolean;
};

// Upper bound on idle (reclaimed, unused) roots kept for reuse. Sized to hold a
// full grid render pass (~600 cells on a wide grid) so a full invalidate/refresh
// recycles entirely from the pool; beyond this we actually unmount, so a huge
// grid that shrinks doesn't pin unbounded memory (parked entries are cleared to
// empty during idle, so pooled roots hold no content).
const POOL_CAP = 800;

const freePool: Entry[] = []; // reclaimed roots, content cleared, ready to reuse
const liveEntries = new Set<Entry>(); // roots currently mounted into a cell host
const hostToEntry = new WeakMap<HTMLElement, Entry>(); // formatter container -> its entry

// Lightweight counters, inspectable via window.__cellRootStats() in dev.
const stats = { createRoot: 0, reuse: 0, render: 0, reclaimed: 0, unmounted: 0 };
if (typeof window !== "undefined") {
  window.__cellRootStats = () => ({
    ...stats,
    poolSize: freePool.length,
    liveSize: liveEntries.size,
    pendingUnmounts: pendingUnmounts.length,
  });
}

// Idle-drained teardown queue for pool overflow. Unmounting many roots at once
// would block a frame; spread it across idle time.
const pendingUnmounts: Root[] = [];
let drainScheduled = false;
const FALLBACK_BATCH = 24; // cap per tick when there's no idle deadline (setTimeout path)

function drainUnmounts(deadline?: IdleDeadline): void {
  drainScheduled = false;
  let processed = 0;
  while (pendingUnmounts.length > 0) {
    if (processed > 0) {
      const outOfBudget = deadline ? deadline.timeRemaining() <= 1 : processed >= FALLBACK_BATCH;
      if (outOfBudget) break;
    }
    const root = pendingUnmounts.shift();
    if (!root) break;
    try {
      root.unmount();
      stats.unmounted++;
    } catch {
      // Already torn down or a rare unmount-vs-render race — harmless on teardown.
    }
    processed++;
  }
  if (pendingUnmounts.length > 0) scheduleDrain();
}

function scheduleDrain(): void {
  if (drainScheduled) return;
  drainScheduled = true;
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(drainUnmounts, { timeout: 250 });
  } else {
    setTimeout(drainUnmounts, 40);
  }
}

// A layout-invisible wrapper that permanently hosts one reusable root.
function makeInnerHost(): HTMLElement {
  const inner = document.createElement("div");
  inner.style.display = "contents";
  return inner;
}

/**
 * Return the root mounted into `host` to the pool: detach it and pool it WITHOUT
 * clearing — the old fiber tree stays mounted (marked `dirty`).
 *
 * Why no `render(null)` here: reclaim happens on the scroll hot path, and during
 * a fling every reclaimed root is reused immediately, so clear-then-render was
 * two scheduled React passes per cell where one suffices — reuse renders the new
 * element directly and React reconciles the old tree away in that single pass.
 * (React's scheduler may commit that render AFTER a paint, so the reused entry is
 * kept `visibility:hidden` until commit — see {@link RevealOnCommit} in
 * mountCellReact. The entry stays `dirty` until then.)
 * Entries that PARK in the pool (scroll stopped) still hold their old tree's
 * effects/subscriptions — {@link scheduleIdleClear} releases those during idle.
 */
function reclaimHost(host: HTMLElement): void {
  const entry = hostToEntry.get(host);
  if (!entry) {
    return;
  }
  hostToEntry.delete(host);
  liveEntries.delete(entry);
  entry.host = null;
  entry.inner.remove();
  entry.dirty = true;
  stats.reclaimed++;
  if (freePool.length < POOL_CAP) {
    freePool.push(entry);
    scheduleIdleClear();
  } else {
    pendingUnmounts.push(entry.root);
    scheduleDrain();
  }
}

// Idle sweep: clear parked dirty entries (render(null) releases the old tree's
// subscriptions/DOM) so nothing stays mounted long after scrolling stops.
let idleClearScheduled = false;
function clearParked(deadline?: IdleDeadline): void {
  idleClearScheduled = false;
  let processed = 0;
  for (const entry of freePool) {
    if (!entry.dirty) continue;
    if (processed > 0) {
      const outOfBudget = deadline ? deadline.timeRemaining() <= 1 : processed >= FALLBACK_BATCH;
      if (outOfBudget) break;
    }
    entry.dirty = false;
    entry.root.render(null);
    processed++;
  }
  if (freePool.some((e) => e.dirty)) scheduleIdleClear();
}

function scheduleIdleClear(): void {
  if (idleClearScheduled) return;
  idleClearScheduled = true;
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(clearParked, { timeout: 500 });
  } else {
    setTimeout(clearParked, 100);
  }
}

// Entries mounted during the CURRENT task. SlickGrid builds new rows in a
// detached fragment and attaches them only at the end of its render task, so
// these hosts look `!isConnected` while their row is still being built — they
// must NEVER be treated as recycled (reclaiming them steals the inner out of
// cells about to appear: blank grid / other cells' content shuffled around).
// The set is emptied by the end-of-task microtask in {@link scheduleSweep}.
const mountedThisTask = new Set<Entry>();

/**
 * Reclaim every root whose cell SlickGrid has recycled (host detached from DOM),
 * skipping entries mounted in the current task (see {@link mountedThisTask}).
 * With that exclusion this is safe to call synchronously from mountCellReact:
 * hosts detached earlier in this task (a full invalidate pass detaches all rows
 * before rebuilding) are genuinely recycled and refill the pool immediately.
 */
function reclaimDisconnected(): void {
  for (const entry of liveEntries) {
    if (entry.host && !entry.host.isConnected && !mountedThisTask.has(entry)) {
      reclaimHost(entry.host);
    }
  }
}

// End-of-task sweep: one microtask per mount burst. Runs after SlickGrid's
// render task has attached the rows it was building — clears the current-task
// set and reclaims anything that ended the task detached.
let sweepScheduled = false;
function scheduleSweep(): void {
  if (sweepScheduled) return;
  sweepScheduled = true;
  queueMicrotask(() => {
    sweepScheduled = false;
    mountedThisTask.clear();
    reclaimDisconnected();
  });
}

/**
 * Keeps a reused dirty root's previous content invisible until the NEW element's
 * render commits. The layout effect fires at commit time, before the browser
 * paints, so the reveal is never visible mid-swap. Runs on every commit (no dep
 * array) so re-renders of the same root always end revealed.
 */
function RevealOnCommit({ inner, children }: { inner: HTMLElement; children?: ReactNode }): ReactNode {
  useLayoutEffect(() => {
    inner.style.visibility = "";
  });
  return children;
}

/**
 * Formatter side — render a React element into a cell. `container` is the
 * formatter's own (optionally pre-styled) element; the reusable root lives in a
 * `display:contents` inner appended inside it, so `container`'s styles/structure
 * are preserved. Returns `container` for the formatter to return to SlickGrid.
 */
export function mountCellReact(
  element: ReactNode,
  container: HTMLElement = document.createElement("div"),
): HTMLElement {
  // Same container re-rendered (mount-once header cell, or in-place updateCell):
  // reuse its existing root directly.
  const existing = hostToEntry.get(container);
  if (existing) {
    existing.root.render(element);
    stats.render++;
    return container;
  }

  let entry = freePool.pop();
  if (!entry) {
    // Pool empty: reclaim hosts recycled earlier in this task (safe — entries
    // mounted in the current task are excluded), then retry before growing.
    reclaimDisconnected();
    entry = freePool.pop();
  }
  if (entry) {
    stats.reuse++;
  } else {
    // Genuinely need a new root (pool growing to the working set).
    const inner = makeInnerHost();
    entry = { inner, root: createRoot(inner), host: null };
    stats.createRoot++;
  }

  let content = element;
  if (entry.dirty) {
    // The pooled root still holds another cell's tree. Hide it until the new
    // render commits so stale content can never show inside this cell.
    entry.inner.style.visibility = "hidden";
    content = createElement(RevealOnCommit, { inner: entry.inner }, element);
  } else {
    entry.inner.style.visibility = "";
  }

  container.appendChild(entry.inner);
  entry.host = container;
  entry.dirty = false; // the incoming render owns the tree now
  hostToEntry.set(container, entry);
  liveEntries.add(entry);
  mountedThisTask.add(entry); // protect from the sync sweep until rows attach

  entry.root.render(content);
  stats.render++;
  scheduleSweep();
  return container;
}

/**
 * Grid teardown — reclaim every root hosted in this grid (and any already
 * detached) so nothing stays mounted, holding subscriptions, after the grid's
 * DOM is destroyed. Reclaimed roots return to the shared pool for reuse by other
 * grids; the pool cap bounds how many linger.
 */
export function unmountAllCellRoots(gridContainer: HTMLElement): void {
  for (const entry of liveEntries) {
    const host = entry.host;
    if (host && (!host.isConnected || gridContainer.contains(host))) {
      reclaimHost(host);
    }
  }
}
